import { uniqBy } from 'lodash';
import {
  InstrumentComponents,
  InstrumentId,
  InstrumentParams,
  InstrumentType,
} from '../../types/common';
import { InstrumentFragment } from './index.types';
import { InstrumentSchema } from '../../types/common';
import { plugins } from '../../plugins';
import { ClearingDepositInstrumentType } from '../../types/gql';

export interface TabSchema<T extends InstrumentId = InstrumentId> extends InstrumentSchema {
  id: T;
  parameters: InstrumentParams;
}

export const mapInstruments = (instruments: InstrumentFragment[]) => {
  const availableInstruments = instruments
    .map((instrument) => ({
      id: mapInstrumentId(instrument),
      parameters: mapParameters(instrument),
    }))
    .map(({ id, parameters }) => {
      // @ts-ignore
      const schema = plugins.find((schema) => schema.supports.has(id));

      return schema ? { id, ...schema, parameters } : null;
    })
    .filter(Boolean) as TabSchema[];

  const idToTab = availableInstruments.reduce((result, { id, components }) => {
    result[id] = {
      ...components,
      available: availableInstruments.filter((item) => item.components === components),
    };
    return result;
  }, {} as Record<InstrumentId, InstrumentComponents & { available: TabSchema[] }>);

  const tabs = uniqBy(availableInstruments, (instrument) => instrument.components).map(
    (instrument) => {
      const { available } = idToTab[instrument.id];
      return {
        firstAvailable: available[0]?.id,
        fee: available.length === 1 ? available[0].parameters.fee : undefined,
        supports: instrument.supports,
        Menu: instrument.components.Menu,
      };
    }
  );

  return {
    tabs,
    idToTab,
  };
};

export const mapInstrumentType = (instrument: InstrumentFragment): InstrumentType => {
  if (instrument.type === ClearingDepositInstrumentType.ProviderWalletTransfer) {
    if (instrument.details?.__typename === 'ClearingProviderWalletTransferPaymentSystem') {
      switch (instrument.details.name) {
        case 'payeer':
          return 'PAYEER';
        case 'advcash-utcom':
          return 'ADVCASH';
      }
    }
  }
  return instrument.type;
};

export const mapParameters = ({
  maximum,
  minimum,
  details,
}: InstrumentFragment): InstrumentParams => ({
  minimum: minimum || 0,
  maximum: maximum || Number.MAX_SAFE_INTEGER,
  fee: details && 'fee' in details ? details.fee : 0,
  paymentSystemId: details && 'id' in details ? details.id : undefined,
});

export const mapInstrumentId = (instrument: InstrumentFragment): InstrumentId =>
  `${mapInstrumentType(instrument)}_${instrument.currency}` as InstrumentId;
